import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import Input from '../../components/UI/Input';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import { ProfileFormData } from '../../types';
import { IDENTITY_PROVIDERS, readExternalPendingProfile } from '../../config/identityProviders';
import { buildUserInfoJson } from '../../utils/userInfoJson';
import { readLegalConsent, writeLegalConsent } from '../../utils/legalConsent';
import { Link } from 'react-router-dom';

const profileSchema = z.object({
  username: z.string().min(3, 'Username must be at least 3 characters'),
  mobile: z.string().min(10, 'Please enter a valid mobile number'),
  linkedInUrl: z
    .string()
    .optional()
    .refine((value) => !value || value.trim().length === 0 || /^https?:\/\/.+/i.test(value.trim()), {
      message: 'Enter a valid LinkedIn URL',
    }),
  college: z.string().optional(),
  degree: z.string().optional(),
  educationStatus: z.enum(['', 'completed', 'pursuing']).optional(),
  isWorking: z.boolean().optional(),
  companyName: z.string().optional(),
  role: z.string().optional(),
  location: z.string().optional(),
  address: z.string().optional(),
  termsAccepted: z.boolean().refine((v) => v === true, {
    message: 'You must accept the Terms of Service',
  }),
  privacyAccepted: z.boolean().refine((v) => v === true, {
    message: 'You must accept the Privacy Policy',
  }),
});

const selectClassName =
  'block w-full rounded-md border-2 border-gray-300 bg-white px-4 py-2.5 text-base text-gray-900 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-20';

const ProfileSetup: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { createProfile } = useAuth();

  const pending = readExternalPendingProfile();
  const storedLegal = readLegalConsent();
  const linkedInMobileLocked = pending?.identityProviderId === IDENTITY_PROVIDERS.LinkedIn
    && Boolean(pending.mobile);

  const { register, handleSubmit, watch, formState: { errors } } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      username: pending?.givenName ?? '',
      mobile: pending?.mobile ?? '',
      linkedInUrl: '',
      educationStatus: '',
      isWorking: false,
      termsAccepted: storedLegal.termsAccepted,
      privacyAccepted: storedLegal.privacyAccepted,
    },
  });

  const isWorking = watch('isWorking');

  const onSubmit = async (data: ProfileFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      writeLegalConsent({ termsAccepted: true, privacyAccepted: true });
      const userInfoJson = buildUserInfoJson({
        college: data.college,
        degree: data.degree,
        educationStatus: data.educationStatus,
        isWorking: data.isWorking,
        companyName: data.companyName,
        role: data.role,
        location: data.location,
        address: data.address,
      });
      const success = await createProfile(
        data.username,
        data.mobile,
        data.linkedInUrl?.trim() || undefined,
        userInfoJson,
        { termsAccepted: true, privacyAccepted: true }
      );

      if (!success) {
        setError('Failed to create profile. Please try again.');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An error occurred. Please try again.';
      setError(message);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100dvh-4.5rem)] flex flex-col justify-center py-10 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <h2 className="text-3xl font-extrabold text-gray-900">Complete Your Profile</h2>
          <p className="mt-2 text-gray-600">
            Please provide the following information to complete your registration
          </p>
        </motion.div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card className="py-8 px-4 sm:px-10">
            {error && (
              <div className="mb-4 p-3 bg-error-50 text-error-600 text-sm rounded-md border border-error-200">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Account</h3>
                <div className="mt-3 space-y-4">
                  <Input
                    id="username"
                    type="text"
                    label="Username"
                    error={errors.username?.message}
                    {...register('username')}
                  />
                  <Input
                    id="mobile"
                    type="tel"
                    label={linkedInMobileLocked ? 'Mobile Number (verified from LinkedIn)' : 'Mobile Number'}
                    error={errors.mobile?.message}
                    readOnly={linkedInMobileLocked}
                    {...register('mobile')}
                  />
                  <Input
                    id="linkedInUrl"
                    type="url"
                    label="LinkedIn URL (optional)"
                    placeholder="https://www.linkedin.com/in/your-profile"
                    error={errors.linkedInUrl?.message}
                    {...register('linkedInUrl')}
                  />
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Education (optional)</h3>
                <div className="mt-3 grid sm:grid-cols-2 gap-4">
                  <Input id="college" type="text" label="College" {...register('college')} />
                  <Input id="degree" type="text" label="Degree" {...register('degree')} />
                  <div className="mb-4 w-full">
                    <label htmlFor="educationStatus" className="block text-sm font-semibold text-gray-900 mb-1.5">
                      Completed or pursuing
                    </label>
                    <select id="educationStatus" className={selectClassName} {...register('educationStatus')}>
                      <option value="">Select status</option>
                      <option value="completed">Completed</option>
                      <option value="pursuing">Pursuing</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Employment (optional)</h3>
                <div className="mt-3 space-y-4">
                  <label className="flex items-center gap-2 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      {...register('isWorking', { setValueAs: (value) => value === true || value === 'on' })}
                    />
                    Currently working
                  </label>
                  {isWorking && (
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Input id="companyName" type="text" label="Company name" {...register('companyName')} />
                      <Input id="role" type="text" label="Role" {...register('role')} />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Location (optional)</h3>
                <div className="mt-3 grid sm:grid-cols-2 gap-4">
                  <Input id="location" type="text" label="Location" {...register('location')} />
                  <Input id="address" type="text" label="Address for communication" {...register('address')} />
                </div>
              </div>

              <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input type="checkbox" className="mt-1" {...register('termsAccepted')} />
                  <span>
                    I agree to the{' '}
                    <Link to="/terms" target="_blank" className="font-medium text-primary-700 hover:text-primary-800">
                      Terms of Service
                    </Link>
                  </span>
                </label>
                {errors.termsAccepted && <p className="text-sm text-error-600">{errors.termsAccepted.message}</p>}
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input type="checkbox" className="mt-1" {...register('privacyAccepted')} />
                  <span>
                    I agree to the{' '}
                    <Link to="/privacy" target="_blank" className="font-medium text-primary-700 hover:text-primary-800">
                      Privacy Policy
                    </Link>
                  </span>
                </label>
                {errors.privacyAccepted && <p className="text-sm text-error-600">{errors.privacyAccepted.message}</p>}
              </div>

              <div>
                <Button type="submit" fullWidth isLoading={isLoading}>
                  Continue
                </Button>
              </div>
            </form>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default ProfileSetup;
